import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { TermoDefinicaoPrazoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-definicao-prazo-pj/termo-definicao-prazo-pj.service';
import { ReportTermoDefinicaoPrazoPjRequest, ReportTermoDefinicaoPrazoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-definicao-prazo-pj/termo-definicao-prazo-pj.model';
import { TermoDefinicaoPrazoPjJobService } from '@/core/services/termo-definicao-prazo-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportTermoDefinicaoPrazoPjRequest;
type Resp = ReportTermoDefinicaoPrazoPjResponse;

@Component({
    selector: 'app-termo-definicao-prazo-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './termo-definicao-prazo-pj.html',
    styleUrl: './termo-definicao-prazo-pj.scss'
})
/**
 * Relatório PDF assíncrono: Termo de Definição de Prazo PJ (compromisso do diretor
 * técnico de entrega de documentação pendente em até 120 dias).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`), sem filtros adicionais.
 */
export class ReportTermoDefinicaoPrazoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly termoService = inject(TermoDefinicaoPrazoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly termoJobService = inject(TermoDefinicaoPrazoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.termoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.termoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o termo.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.termoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
